import { store } from '../state/store';
import type { McpToolDef, McpToolResult } from '../types';

const MCP_PROTOCOL_VERSION = '2025-06-18';
const JSONRPC_VERSION = '2.0';

let requestIdCounter = 0;

function nextId(): number {
  return ++requestIdCounter;
}

function getMcpUrl(): string {
  return `${store.getState().config.serverUrl}/mcp`;
}

/** Parse a fetch Response that may be JSON or SSE (text/event-stream). */
async function parseResponse(response: Response): Promise<Record<string, any>> {
  const ct = response.headers.get('content-type') || '';
  if (ct.includes('text/event-stream')) {
    const text = await response.text();
    for (const line of text.split('\n')) {
      if (line.startsWith('data: ')) {
        try { return JSON.parse(line.slice(6)); } catch { /* skip */ }
      }
    }
    return {};
  }
  return response.json();
}

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json, text/event-stream',
  };
  const token = store.getState().auth.token;
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

function filterConfiguredTools(tools: McpToolDef[]): McpToolDef[] {
  const { allowedTools, dangerousTools } = store.getState().config;
  const activeToolIds = new Set([...(allowedTools ?? []), ...(dangerousTools ?? [])]);

  if (activeToolIds.size === 0) {
    return [];
  }

  return tools.filter((tool) => tool.id === 0 || activeToolIds.has(tool.id));
}

export class McpClient {
  private sessionId: string | null = null;

  getSessionId(): string | null {
    return this.sessionId;
  }

  async initialize(): Promise<void> {
    const url = getMcpUrl();
    const body = {
      jsonrpc: JSONRPC_VERSION,
      id: nextId(),
      method: 'initialize',
      params: {
        protocolVersion: MCP_PROTOCOL_VERSION,
        capabilities: {},
        clientInfo: {
          name: 'mcp-chat-widget',
          version: '1.0.0',
        },
      },
    };

    console.debug('[mcp-chat-widget] MCP initialize:', url);

    const response = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errText = await response.text().catch(() => '');
      throw new Error(`MCP initialize failed: ${response.status} ${errText}`);
    }

    // Extract session ID from response header
    const sid = response.headers.get('mcp-session-id');
    if (sid) {
      this.sessionId = sid;
      store.setMcpSession(sid);
    }

    const result = await parseResponse(response);
    if (result.error) {
      throw new Error(`MCP initialize error: ${result.error.message}`);
    }

    console.debug('[mcp-chat-widget] MCP initialized, session:', this.sessionId, 'result:', result.result);

    // Send initialized notification (required by MCP protocol)
    await this.sendNotification('notifications/initialized', {});
  }

  async listTools(): Promise<McpToolDef[]> {
    const result = await this.request('tools/list', {});
    const discoveredTools: McpToolDef[] = (result.tools || []).map((t: Record<string, unknown>) => ({
      id: t.id != null ? Number(t.id) : 0,
      name: t.name as string,
      title: (t.title as string) || undefined,
      description: (t.description as string) || '',
      inputSchema: (t.inputSchema as Record<string, unknown>) || {},
      annotations: (t.annotations as Record<string, unknown>) || undefined,
      path: (t.path as string) || undefined,
      httpMethod: (t.httpMethod as string) || undefined,
      queryMap: (t.queryMap as McpToolDef['queryMap']) || undefined,
      bodyTemplate: (t.bodyTemplate as string) || undefined,
      formEnabled: (t.formEnabled as boolean) ?? true,
    }));
    const tools = filterConfiguredTools(discoveredTools);
    store.setTools(tools);
    console.debug(
      `[mcp-chat-widget] MCP discovered ${discoveredTools.length} tools, ${tools.length} active for this widget:`,
      tools.map((t) => t.name),
    );
    return tools;
  }

  async callTool(name: string, args: Record<string, unknown>): Promise<McpToolResult> {
    console.debug(`[mcp-chat-widget] MCP calling tool: ${name}`, args);
    const result = await this.request('tools/call', { name, arguments: args });
    return {
      content: result.content || [],
      isError: result.isError || false,
    };
  }

  async close(): Promise<void> {
    if (!this.sessionId) return;

    try {
      const url = getMcpUrl();
      await fetch(url, {
        method: 'DELETE',
        headers: {
          ...getAuthHeaders(),
          'mcp-session-id': this.sessionId,
        },
        credentials: 'include',
      });
      console.debug('[mcp-chat-widget] MCP session closed:', this.sessionId);
    } catch (err) {
      console.warn('[mcp-chat-widget] MCP close error:', err);
    } finally {
      this.sessionId = null;
      store.setMcpSession(null);
      store.setTools([]);
    }
  }

  private async request(method: string, params: Record<string, unknown>): Promise<Record<string, any>> {
    if (!this.sessionId) {
      throw new Error('MCP session not initialized');
    }

    const url = getMcpUrl();
    const body = {
      jsonrpc: JSONRPC_VERSION,
      id: nextId(),
      method,
      params,
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'mcp-session-id': this.sessionId,
      },
      credentials: 'include',
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errText = await response.text().catch(() => '');
      throw new Error(`MCP ${method} failed: ${response.status} ${errText}`);
    }

    const json = await parseResponse(response);
    if (json.error) {
      throw new Error(`MCP ${method} error: ${json.error.message || JSON.stringify(json.error)}`);
    }

    return json.result || {};
  }

  private async sendNotification(method: string, params: Record<string, unknown>): Promise<void> {
    if (!this.sessionId) return;

    const url = getMcpUrl();
    const body = {
      jsonrpc: JSONRPC_VERSION,
      method,
      params,
    };

    try {
      await fetch(url, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'mcp-session-id': this.sessionId,
        },
        credentials: 'include',
        body: JSON.stringify(body),
      });
    } catch {
      // Notifications are fire-and-forget
    }
  }
}
